import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import CreateRole from '../../components/CreateRole/CreateRole'
import { Form } from '@/ui/form'
import { Loader } from '@/shared/components/Loader/Loader'
import { useRoleModulesQuery, useRoleDetailQuery } from '../../queries/useRoleQueries'
import {
  useCreateRoleMutation,
  useUpdateRoleMutation,
} from '../../mutations/useRoleMutations'
import { roleFormSchema, type RoleFormValues } from '../../schemas/role.schema'
import {
  applyFeatureToggle,
  applyModuleToggle,
  buildPermissionTree,
  buildRoleFeatureAccess,
  buildSelectionFromDetail,
  hasAnyAccess,
  type FeatureSelection,
} from '../../utils/permissionTree'
import styles from '../RolePage/Rolepage.module.scss'

type Props = {
  mode: 'create' | 'edit'
  roleId?: string
}

export default function RoleFormPage({ mode, roleId }: Props) {
  const navigate = useNavigate()
  const isEditMode = mode === 'edit'
  const editingRoleId = isEditMode ? Number(roleId) : 0

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: { title: '', description: '' },
  })

  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([])
  const [featureSelection, setFeatureSelection] = useState<FeatureSelection>({})

  const { data: moduleNodes, isLoading: modulesLoading } = useRoleModulesQuery()
  const {
    data: roleDetail,
    isLoading: detailLoading,
  } = useRoleDetailQuery(editingRoleId, { enabled: isEditMode && editingRoleId > 0 })

  const tree = useMemo(
    () => buildPermissionTree(moduleNodes ?? []),
    [moduleNodes]
  )

  // Prefill the form once the role detail (Edit mode) arrives.
  const prefilledRef = useRef<number | null>(null)
  useEffect(() => {
    if (!isEditMode || !roleDetail) return
    if (prefilledRef.current === roleDetail.id) return
    prefilledRef.current = roleDetail.id

    form.reset({
      title: roleDetail.title ?? '',
      description: roleDetail.description ?? '',
    })

    const selection = buildSelectionFromDetail(roleDetail)
    setFeatureSelection(selection)
    setSelectedModuleIds(
      roleDetail.role_feature_access
        .filter((module) =>
          module.features.some((feature) => feature.has_access)
        )
        .map((module) => module.module_id)
    )
  }, [isEditMode, roleDetail, form])

  const createRoleMutation = useCreateRoleMutation({
    onSuccess: () => {
      toast.success('Role created successfully')
      navigate('/roles/active')
    },
    onError: (error) => toast.error(error.message),
  })

  const updateRoleMutation = useUpdateRoleMutation({
    onSuccess: () => {
      toast.success('Role updated successfully')
      navigate('/roles/active')
    },
    onError: (error) => toast.error(error.message),
  })

  const isSaving = createRoleMutation.isPending || updateRoleMutation.isPending

  const handleModuleAssignChange = (moduleId: number, assigned: boolean) => {
    setSelectedModuleIds((prev) =>
      assigned
        ? [...new Set([...prev, moduleId])]
        : prev.filter((id) => id !== moduleId)
    )
    // Un-assigning a module revokes every feature it granted.
    if (!assigned) {
      setFeatureSelection((prev) =>
        applyModuleToggle(tree, prev, moduleId, false)
      )
    }
  }

  const handleSelectAllModules = (assigned: boolean) => {
    if (assigned) {
      setSelectedModuleIds(tree.map((module) => module.moduleId))
      return
    }
    setSelectedModuleIds([])
    setFeatureSelection({})
  }

  const handleFeatureToggle = (featureId: number, checked: boolean) => {
    setFeatureSelection((prev) =>
      applyFeatureToggle(tree, prev, featureId, checked)
    )
  }

  const handleModuleFeaturesToggle = (moduleId: number, checked: boolean) => {
    setFeatureSelection((prev) =>
      applyModuleToggle(tree, prev, moduleId, checked)
    )
  }

  const onSubmit = (values: RoleFormValues) => {
    if (!hasAnyAccess(featureSelection)) {
      toast.error('Grant at least one feature for this role')
      return
    }

    const body = {
      title: values.title.trim(),
      description: values.description?.trim() || '',
      role_feature_access: buildRoleFeatureAccess(tree, featureSelection),
    }

    if (isEditMode) {
      updateRoleMutation.mutate({ roleId: editingRoleId, body })
    } else {
      createRoleMutation.mutate(body)
    }
  }

  const isInitialLoading =
    modulesLoading || (isEditMode && detailLoading)

  if (isInitialLoading) {
    return <Loader text="Loading role form..." />
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className={styles.tableCard}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/roles/active')}
          >
            <span>‹ Back to Roles</span>
          </button>

          <CreateRole
            title={isEditMode ? 'Edit Role' : 'Create Role'}
            control={form.control}
            tree={tree}
            treeLoading={modulesLoading}
            selectedModuleIds={selectedModuleIds}
            featureSelection={featureSelection}
            onModuleAssignChange={handleModuleAssignChange}
            onSelectAllModules={handleSelectAllModules}
            onFeatureToggle={handleFeatureToggle}
            onModuleFeaturesToggle={handleModuleFeaturesToggle}
          />

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => navigate('/roles/active')}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={isSaving}
            >
              {isSaving
                ? 'Saving...'
                : isEditMode
                  ? 'Update Role'
                  : 'Create Role'}
            </button>
          </div>
        </div>
      </form>
    </Form>
  )
}
